import type { FieldMetadata, ViewFilterNode } from '../../../data/client';
import { describeViewFilterParts, type RelationContext } from '../../filters/filterModel';

/**
 * THE FILTER, AS A ROW OF REMOVABLE TOKENS (spec/screens/list.md section 2 item 6: "one
 * token per active filter, each removable").
 *
 * A token is a TOP-LEVEL child of the stored filter, and the sentence on it is the one the
 * filter button already printed for that child - so the row and the editor cannot describe
 * the same condition in two different registers.
 *
 * AN "OR" OR A NEGATED GROUP AT THE TOP IS ONE TOKEN, NOT SEVERAL. Tokens sitting side by
 * side read as "and": splitting `A or B` into two of them would put a claim on the screen
 * that the filter does not make, and removing one would silently widen the list, which is
 * the failure this module's neighbours exist to prevent. `not (A and B)` is the same trap
 * one operator along. Both therefore show as a single token describing the whole of it, and
 * the way to change either is the editor.
 *
 * A TOKEN HAS TWO HALVES, AND ONLY ONE OF THEM GIVES (wave 3 round 2, brief item 2). The
 * parent photographed "Status is one of Onboarding, Active" drawn as "S.." in an 81px box
 * with the pane at its cap - a token that has truncated its own field name narrows the list
 * by something the reader cannot name, which is worse than no token. So the FIELD NAME AND
 * THE OPERATOR are the head and are never cut, the OPERAND is what wears the ellipsis, and
 * the token's minimum width is therefore its head. When the strip cannot hold every token
 * even at that minimum it scrolls sideways (`.list-token-strip`), which is the only part of
 * the row that moves.
 */
export interface FilterToken {
  /** Which top-level child this is; the index the removal is addressed by. */
  readonly index: number;
  /** The whole sentence: what the title attribute and the remove control announce. */
  readonly label: string;
  /** The field's name and the operator. Never truncated. */
  readonly head: string;
  /** The operand, truncated first. `undefined` for a nullary operator ("Owner is empty"). */
  readonly value: string | undefined;
  /** False for the single token that stands for a whole OR filter: it has no part to drop. */
  readonly removable: boolean;
}

function token(
  index: number,
  node: ViewFilterNode,
  fields: readonly FieldMetadata[],
  relations: RelationContext,
  removable: boolean,
): FilterToken {
  const parts = describeViewFilterParts(node, fields, relations);
  return {
    index,
    label: parts.value === undefined ? parts.head : `${parts.head} ${parts.value}`,
    head: parts.head,
    value: parts.value,
    removable,
  };
}

export function filterTokens(
  node: ViewFilterNode | null,
  fields: readonly FieldMetadata[],
  relations: RelationContext,
): readonly FilterToken[] {
  if (node === null) return [];
  if (node.kind !== 'group') return [token(0, node, fields, relations, true)];
  if (node.combinator === 'or' || node.negate === true) {
    return [token(0, node, fields, relations, false)];
  }
  return node.children.map((child, index) => token(index, child, fields, relations, true));
}

/**
 * Removing a token returns the filter WITHOUT that child, and `null` when nothing is left -
 * which is what "no filter" is on the wire, so an emptied row cannot leave behind a group
 * with no children for the compiler to interpret.
 *
 * A GROUP OF ONE COLLAPSES to its remaining child rather than staying a one-child group:
 * the two mean the same thing to the compiler, and the collapsed form is what the editor
 * writes, so a filter that has been through this row stays byte-comparable with one that
 * has not - which is what the view's own dirty flag is measured with.
 */
export function removeFilterToken(
  node: ViewFilterNode | null,
  index: number,
): ViewFilterNode | null {
  if (node === null) return null;
  if (node.kind !== 'group') return index === 0 ? null : node;
  if (node.combinator === 'or' || node.negate === true) return index === 0 ? null : node;
  const children = node.children.filter((_, position) => position !== index);
  if (children.length === 0) return null;
  if (children.length === 1) return children[0] ?? null;
  return { ...node, children };
}
